<template>
  <div id="client-document">
    <!-- Barre d'actions, comme en tête de la fiche devis de l'ERP -->
    <div class="flex flex-wrap items-center mb-base">
      <vs-button type="border" icon-pack="feather" icon="icon-arrow-left" class="mr-4 mb-2" @click="back">{{ t('back') }}</vs-button>
      <vs-button v-if="doc && doc.has_pdf" icon-pack="feather" icon="icon-file-text" class="mr-4 mb-2" :disabled="!!opening[pdfKey]" @click="viewPdf(kind, doc.id)">{{ t('view_pdf') }}</vs-button>
      <span v-else-if="doc" class="cp-muted mb-2">{{ t('pdf_unavailable') }}</span>
    </div>

    <vx-card v-if="loading || loadError">
      <div v-if="loading" class="cp-state">
        <feather-icon icon="LoaderIcon" svgClasses="h-6 w-6" class="cp-spin" />
        <div>{{ t('loading') }}</div>
      </div>
      <div v-else class="cp-state">
        <vs-alert color="danger" active="true">{{ loadError }}</vs-alert>
        <vs-button size="small" class="mt-4" @click="load">{{ t('retry') }}</vs-button>
      </div>
    </vx-card>

    <template v-else-if="doc">
      <div class="vx-row">
        <!-- 1. Le document -->
        <div class="vx-col lg:w-4/12 w-full">
          <vx-card :title="heading" class="mb-base">
            <table class="cp-kv">
              <tr>
                <td>{{ isOrder ? t('ref_xylo') : t('invoice_no') }}</td>
                <td>{{ isOrder ? `#${doc.id}` : doc.invoice_no }}</td>
              </tr>
              <tr v-if="doc.ref_client">
                <td>{{ t('your_ref') }}</td>
                <td>{{ doc.ref_client }}</td>
              </tr>
              <tr>
                <td>{{ isOrder ? t('status') : t('type') }}</td>
                <td>
                  <vs-chip v-if="isOrder" :color="statusColor(doc.status)">{{ t('status_' + doc.status) }}</vs-chip>
                  <vs-chip v-else :color="doc.kind === 'credit_note' ? 'danger' : doc.kind === 'advance' ? 'warning' : 'success'">{{ t('kind_' + doc.kind) }}</vs-chip>
                  <vs-chip v-if="doc.awaiting_confirmation" color="warning">{{ t('awaiting_confirmation') }}</vs-chip>
                </td>
              </tr>
              <tr v-if="doc.availability">
                <td>{{ t('availability') }}</td>
                <td>
                  <vs-chip :color="doc.availability === 'in_stock' ? 'success' : 'warning'">{{ doc.availability === 'in_stock' ? t('in_stock') : t('to_prepare') }}</vs-chip>
                </td>
              </tr>
              <tr>
                <td>{{ isOrder ? t('order_date') : t('date') }}</td>
                <td>{{ date(doc.date) }}</td>
              </tr>
              <tr v-if="doc.date_approved">
                <td>{{ t('approval_date') }}</td>
                <td>{{ date(doc.date_approved) }}</td>
              </tr>
              <tr v-if="doc.date_delivery_desired">
                <td>{{ t('requested_delivery') }}</td>
                <td>{{ date(doc.date_delivery_desired) }}</td>
              </tr>
              <tr v-if="doc.confirmed_delivery">
                <td>{{ t('confirmed_delivery') }}</td>
                <td>{{ doc.confirmed_delivery }}</td>
              </tr>
              <tr v-if="doc.advance_pct">
                <td>{{ t('advance_payment') }}</td>
                <td>{{ doc.advance_pct }} %</td>
              </tr>
              <tr v-if="doc.incoterms">
                <td>{{ t('incoterms') }}</td>
                <td>{{ doc.incoterms }}</td>
              </tr>
              <tr v-if="isOrder">
                <td>{{ t('fsc') }}</td>
                <td><vs-chip :color="doc.fsc ? 'success' : ''">{{ doc.fsc ? t('yes') : t('no') }}</vs-chip></td>
              </tr>
              <tr v-if="!isOrder && doc.idorder">
                <td>{{ t('order') }}</td>
                <td><span class="cp-link" @click="open('client-order', doc.idorder)">#{{ doc.idorder }}</span></td>
              </tr>
              <tr>
                <td>{{ t('total_volume') }}</td>
                <td>{{ (doc.totals.volume || 0).toFixed(2) }} m³</td>
              </tr>
              <tr>
                <td>{{ t('total_excl') }}</td>
                <td class="font-semibold">{{ amount(doc.totals.ht, doc.totals.currency) }}</td>
              </tr>
              <tr>
                <td>{{ t('total_incl') }}</td>
                <td class="font-semibold">{{ amount(doc.totals.ttc, doc.totals.currency) }}</td>
              </tr>
            </table>
          </vx-card>
        </div>

        <!-- 2. Le client et ses adresses -->
        <div class="vx-col lg:w-4/12 w-full">
          <vx-card :title="t('client')" class="mb-base">
            <table class="cp-kv">
              <tr>
                <td>{{ t('company') }}</td>
                <td>{{ (doc.client && doc.client.name) || doc.company }}</td>
              </tr>
              <tr v-if="doc.client && doc.client.vat_number">
                <td>{{ t('vat_number') }}</td>
                <td>{{ doc.client.vat_number }}</td>
              </tr>
              <tr v-if="doc.client && doc.client.email">
                <td>{{ t('email') }}</td>
                <td>{{ doc.client.email }}</td>
              </tr>
              <tr v-if="doc.client && doc.client.phone">
                <td>{{ t('phone') }}</td>
                <td>{{ doc.client.phone }}</td>
              </tr>
              <tr v-if="doc.billing_address">
                <td>{{ t('billing_address') }}</td>
                <td>
                  <div v-if="doc.billing_address.street">{{ doc.billing_address.street }}</div>
                  <div>{{ [doc.billing_address.postal_code, doc.billing_address.place].filter(Boolean).join(' ') }}</div>
                  <div class="flex items-center">
                    <img height="14" class="mr-2" :src="flagSrc(doc.billing_address.country_code)" alt="" />{{ doc.billing_address.country }}
                  </div>
                </td>
              </tr>
              <tr>
                <td>{{ t('delivery_address') }}</td>
                <td>
                  <template v-if="doc.delivery_address">
                    <div v-if="doc.delivery_address.street">{{ doc.delivery_address.street }}</div>
                    <div>{{ [doc.delivery_address.postal_code, doc.delivery_address.place].filter(Boolean).join(' ') }}</div>
                    <div class="flex items-center">
                      <img height="14" class="mr-2" :src="flagSrc(doc.delivery_address.country_code)" alt="" />{{ doc.delivery_address.country }}
                    </div>
                  </template>
                  <span v-else class="cp-muted">{{ t('no_delivery_address') }}</span>
                </td>
              </tr>
            </table>
            <!-- Carte open source (OpenFreeMap) : usine → adresse de livraison. -->
            <client-delivery-map v-if="mapPosition" :orders="[doc]" :height="240" :clickable="false" class="mt-2" />
          </vx-card>
        </div>

        <!-- 3. Transport et documents -->
        <div class="vx-col lg:w-4/12 w-full">
          <vx-card v-if="isOrder && doc.status !== 'pending'" :title="t('transport')" class="mb-base">
            <table v-if="doc.transport" class="cp-kv">
              <tr v-if="doc.transport.id">
                <td>{{ t('transport_no') }}</td>
                <td>
                  <vs-chip :color="doc.transport.carrier_assigned ? '#A9CAB7' : '#c2d1f0'"><span>#{{ doc.transport.id }}</span>&nbsp;<feather-icon icon="TruckIcon" svgClasses="h-4 w-4" /></vs-chip>
                </td>
              </tr>
              <tr v-if="doc.transport.loading_date">
                <td>{{ t('loading_date') }}</td>
                <td>
                  {{ date(doc.transport.loading_date) }}
                  <span v-if="isToday(doc.transport.loading_date)" class="cp-today ml-2"><i class="cp-today-dot"></i>{{ t('today') }}</span>
                </td>
              </tr>
              <tr v-if="doc.transport.delivery_date">
                <td>{{ t('estimated_delivery') }}</td>
                <td>{{ date(doc.transport.delivery_date) }}</td>
              </tr>
              <tr v-if="doc.transport.truck_plate">
                <td>{{ t('truck_plate') }}</td>
                <td>{{ doc.transport.truck_plate }}</td>
              </tr>
              <tr v-if="doc.transport.trailer_plate">
                <td>{{ t('trailer_plate') }}</td>
                <td>{{ doc.transport.trailer_plate }}</td>
              </tr>
              <tr v-if="doc.transport.aviz">
                <td>{{ t('aviz') }}</td>
                <td>{{ [doc.transport.aviz.serie, doc.transport.aviz.number].filter(Boolean).join(' ') }}</td>
              </tr>
            </table>
            <div v-else class="cp-muted">{{ t('not_planned') }}</div>
          </vx-card>

          <vx-card :title="t('documents')" class="mb-base">
            <vs-button v-if="doc.has_pdf" icon-pack="feather" icon="icon-file" class="mb-4 w-full" :disabled="!!opening[pdfKey]" @click="viewPdf(kind, doc.id)">
              {{ heading }}
            </vs-button>
            <vs-button v-else icon-pack="feather" icon="icon-file" class="mb-4 w-full" disabled>{{ t('pdf_unavailable') }}</vs-button>
            <template v-if="isOrder">
              <div v-for="inv in doc.invoices" :key="inv.id" class="flex items-center mb-3">
                <vs-button type="border" icon-pack="feather" icon="icon-file-text" class="flex-1" :disabled="!!opening['invoice-' + inv.id]" @click="viewPdf('invoice', inv.id)">
                  {{ t(inv.kind === 'advance' ? 'kind_advance' : 'invoice') }} {{ inv.invoice_no }}
                </vs-button>
                <vs-button type="flat" icon-pack="feather" icon="icon-chevron-right" class="ml-2" @click="open('client-invoice', inv.id)" />
              </div>
            </template>
            <vs-button v-else-if="doc.idorder" type="border" icon-pack="feather" icon="icon-chevron-right" class="w-full" @click="open('client-order', doc.idorder)">
              {{ t('order') }} #{{ doc.idorder }}
            </vs-button>
          </vx-card>
        </div>
      </div>

      <!-- Produits : comme le PDF -->
      <div class="vx-row">
        <div class="vx-col w-full">
          <vx-card :title="t('products')" class="mb-base">
            <p v-if="showStock" class="cp-muted text-sm mb-4">{{ t('stock_hint') }}</p>
            <vs-table :data="doc.lines">
              <template slot="thead">
                <vs-th>{{ t('product') }}</vs-th>
                <vs-th v-if="showStock">{{ t('availability') }}</vs-th>
                <vs-th class="cp-num">{{ t('quantity') }}</vs-th>
                <vs-th class="cp-num">{{ t('unit_price') }}</vs-th>
                <vs-th class="cp-num">{{ t('volume_m3') }}</vs-th>
                <vs-th class="cp-num">{{ t('line_total') }}</vs-th>
                <vs-th class="cp-num">{{ t('vat_pct') }}</vs-th>
                <vs-th class="cp-num">{{ t('total_incl') }}</vs-th>
              </template>
              <template slot-scope="{ data }">
                <vs-tr v-for="(l, i) in data" :key="i">
                  <vs-td>{{ l.description }}</vs-td>
                  <vs-td v-if="showStock">
                    <vs-chip v-if="l.stock === 'in_stock'" color="success">{{ t('in_stock') }}</vs-chip>
                    <vs-chip v-else-if="l.stock === 'to_prepare'" color="warning">{{ t('to_prepare') }}</vs-chip>
                    <span v-else class="cp-muted">—</span>
                  </vs-td>
                  <vs-td class="cp-num">{{ l.quantity }}</vs-td>
                  <vs-td class="cp-num">{{ l.unit_price }}</vs-td>
                  <vs-td class="cp-num">{{ l.volume ? l.volume.toFixed(4) : '—' }}</vs-td>
                  <vs-td class="cp-num">{{ l.total }}</vs-td>
                  <vs-td class="cp-num">{{ l.vat_pct === null ? '—' : `${l.vat_pct} %` }}</vs-td>
                  <vs-td class="cp-num">{{ l.total_ttc }}</vs-td>
                </vs-tr>
              </template>
            </vs-table>
            <div class="cp-totals">
              <div>{{ t('total_volume') }} : <span class="strong">{{ (doc.totals.volume || 0).toFixed(2) }} m³</span></div>
              <div>{{ t('total_excl') }} : <span class="strong">{{ amount(doc.totals.ht, doc.totals.currency) }}</span></div>
              <div>{{ t('total_incl') }} : <span class="strong">{{ amount(doc.totals.ttc, doc.totals.currency) }}</span></div>
            </div>
          </vx-card>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
// Portail client → fiche d'un devis / d'une commande (meta.portalKind 'order')
// ou d'une facture ('invoice'), sur le modèle de la fiche devis de l'ERP
// (views/quotation/OperationView.vue) : trois cartes (document, client et
// adresses, transport et documents) puis les produits, lignes et totaux comme
// le PDF (buildDocumentLines côté serveur).
import portalPage from '@/views/client/portalPage.js'
import ClientDeliveryMap from '@/views/client/ClientDeliveryMap.vue'

const FLAGS = require.context('@/assets/images/flags', false, /\.png$/)

export default {
  name: 'ClientDocument',
  components: { ClientDeliveryMap },
  mixins: [portalPage],
  data () {
    return { doc: null }
  },
  computed: {
    kind () { return (this.$route.meta && this.$route.meta.portalKind) || 'order' },
    isOrder () { return this.kind === 'order' },
    pdfKey () { return `${this.kind}-${this.doc ? this.doc.id : ''}` },
    showStock () { return this.isOrder && this.doc && this.doc.status === 'in_progress' },
    heading () {
      if (!this.doc) return ''
      if (!this.isOrder) return `${this.t(`kind_${this.doc.kind}`)} ${this.doc.invoice_no}`
      return `${this.doc.status === 'pending' ? this.t('quotation') : this.t('order')} #${this.doc.id}`
    },
    mapPosition () {
      const d = this.doc && this.doc.delivery
      return d && d.lat !== null && d.lng !== null ? { lat: d.lat, lng: d.lng } : null
    }
  },
  methods: {
    flagSrc (cc) {
      const code = String(cc || '').toUpperCase() === 'GB' ? 'UK' : String(cc || '').toUpperCase()
      try { return FLAGS(`./${code}.png`) } catch (e) { return FLAGS('./UE.png') }
    },
    load () {
      return this.fetch(`/${this.kind}/${encodeURIComponent(this.$route.params.id)}`, (data) => { this.doc = data })
    },
    back () {
      if (window.history.length > 1) this.$router.back()
      else this.$router.push({ name: 'client-orders', query: this.asQuery }).catch(() => {})
    }
  },
  created () {
    this.load()
  }
}
</script>

<style scoped>
.cp-kv {
  width: 100%;
}
.cp-kv td {
  padding: 0 0 0.8rem;
  vertical-align: top;
}
.cp-kv td:first-child {
  padding-right: 1.5rem;
  font-weight: 600;
  white-space: nowrap;
}
</style>
